import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Menu } from '@headlessui/react';

import useToast from '../../../../../state/hooks/useToast';
import useAlert from '../../../../../state/hooks/useAlert';
import { changePageInfo } from '../../../../../state/store/reducers/ui/headerSlice';
import { Button, Pagination } from '../../../../../components/common';
import { ListState } from '../../../../../components/blocks';
import PopoverWithFloat from '../../../../../components/hoc/PopoverWithFloat';
import plansService from '../../../../../services/api/smartbill/plansService';

import PlanListFilters from './PlanListFilters';
import ModalCreatePlan from './ModalCreatePlan';


function PlanListPage() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    const [createPlanModal, setCreatePlanModal] = useState({ open: false, plan: null });

    // get query params
    const query: any = {
        pager: {
            page: Number(searchParams.get('page')) || 1,
        },
        filters: {},
    };
    if (searchParams.get('name')) { query.filters['name'] = searchParams.get('name') || '' }
    if (searchParams.get('isDefault')) { query.filters['isDefault'] = searchParams.get('isDefault') === 'true' }
    if (searchParams.get('active')) { query.filters['isActive'] = searchParams.get('active') === 'true' }

    useEffect(() => {
        dispatch(changePageInfo({ title: "Subscription Plans" }));
    }, []);

    const queryKey = ['plans', query];
    const plansQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => plansService.getPlans({
            page: query.pager.page,
            ...query.filters
        }),
        placeholderData: keepPreviousData,
    });

    const plans = plansQuery.data?.data || [];
    const pager = plansQuery.data?.pager || { page: 1, totalCount: 0, pageSize: 10 };

    const handleFilterChange = (filters: any) => {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('page', '1');

        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key] !== '') {
                newSearchParams.set(key, filters[key]);
            }
        });

        setSearchParams(newSearchParams);
    };

    const handlePageChange = (page: number) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', page.toString());
        setSearchParams(newSearchParams);
    };

    const openCreatePlanModal = (plan = null) => setCreatePlanModal({ open: true, plan });
    const closeCreatePlanModal = () => setCreatePlanModal({ open: false, plan: null });

    const handlePlanCreated = () => {
        // Refetch the plans query to ensure fresh data (same as PlanDetailsPage)
        plansQuery.refetch();
        closeCreatePlanModal();
        addToast({
            type: 'success',
            text: 'Plan updated successfully'
        });
    };

    const deletePlanMutation = useMutation({
        mutationFn: (planId: string) => plansService.deletePlan(planId),
        onSuccess: () => {
            // Refetch the plans query to ensure fresh data (same as PlanDetailsPage)
            plansQuery.refetch();
            addToast({
                type: 'success',
                text: 'Plan deleted successfully'
            });
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                text: error.message || 'Failed to delete plan'
            });
        }
    });

    const handleDeletePlan = (plan: any) => {
        openAlertConfirm({
            title: 'Delete Plan',
            message: `Are you sure you want to delete "${plan.name}"? This action cannot be undone.`,
            onConfirmed: () => {
                deletePlanMutation.mutate(plan.id);
            }
        });
    };

    return (
        <div className=''>
            <div className='flex flex-row-reverse flex-wrap items-start justify-between mb-1'>
                <Button type='button' onClick={() => openCreatePlanModal()} className='mb-4'>Create Plan</Button>
                <PlanListFilters
                    onFilterChange={handleFilterChange}
                    defaultValues={query.filters}
                />
            </div>

            <ListState
                isLoading={plansQuery.isLoading}
                isError={plansQuery.isError}
                listCount={plans.length}
                totalDataCount={pager.totalCount}
                hasSearch={Object.keys(query.filters).some(key => query.filters[key])}
                onTryAgain={() => plansQuery.refetch()}
            />
            
            {!plansQuery.isLoading && !plansQuery.isError && plans.length > 0 && (
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 px-6'>Plan</th>
                                    <th className='py-6 px-6'>Pricing</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Created</th>
                                    <th className='py-6 px-6 text-right'>Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {plans.map((plan: any, index: number) => (
                                    <tr key={plan.id}>
                                        <td className='py-4 px-6'>
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {plan.name}
                                                    {plan.defaultPlan && (
                                                        <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                                                            Default
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                    {plan.description}
                                                </div>
                                            </div>
                                        </td>
                                        <td className='py-4 px-6'>
                                            <div className="font-medium text-gray-900 dark:text-white">
                                                {plan.currency} {plan.pricePerStudent.toFixed(2)}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                per student
                                            </div>
                                        </td>
                                        <td className='py-4 px-6'>
                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full outline-1 outline-offset-1 outline-gray-200 dark:outline-gray-700 ${
                                                plan.active 
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-500' 
                                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-500'
                                            }`}>
                                                {plan.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className='py-4 px-6'>
                                            <div className="text-sm text-gray-900 dark:text-white">
                                                {format(new Date(plan.createdAt), 'MMM dd, yyyy')}
                                            </div>
                                        </td>
                                        <td className='py-4 px-6 text-right'>
                                            <Menu as="div" className="relative inline-block text-left">
                                                <PopoverWithFloat placement='bottom-end' portal>
                                                    <Menu.Button type='button' className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                                                        <i className="ri-more-2-fill"></i>
                                                    </Menu.Button>

                                                    <Menu.Items as='div' unmount={false} className="w-48 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-white/5 overflow-hidden px-2 py-3">
                                                        <Menu.Item
                                                            as={Link}
                                                            to={`/payments/subscriptions/plans/${plan.id}`}
                                                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                                        >
                                                            View Details
                                                        </Menu.Item>
                                                        <Menu.Item
                                                            as="button"
                                                            type='button'
                                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                                            onClick={() => openCreatePlanModal(plan)}
                                                        >
                                                            Edit Plan
                                                        </Menu.Item>
                                                        <Menu.Item
                                                            as="button"
                                                            type='button'
                                                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                                            onClick={() => handleDeletePlan(plan)}
                                                        >
                                                            Delete Plan
                                                        </Menu.Item>
                                                    </Menu.Items>
                                                </PopoverWithFloat>
                                            </Menu>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className='flex flex-row-reverse flex-wrap items-start justify-between mb-1'>
                    <Pagination
                        currentPage={pager.page}
                        totalCount={pager.totalCount}
                        pageSize={pager.pageSize}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </React.Fragment>
            )}

            <ModalCreatePlan
                open={createPlanModal.open}
                plan={createPlanModal.plan}
                onClose={closeCreatePlanModal}
                onSuccess={handlePlanCreated}
            />
        </div>
    );
}

export default PlanListPage;